import React, { useState, useEffect } from 'react';
import { Modal, Button, Typography, Space, Tag, Badge, Empty, Skeleton, message } from 'antd';
import { Plus, Landmark, RefreshCw, ShieldCheck, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import BankIcon from 'react-br-bank-icons';
import { Method_POST_CONTEXTO } from '../../../../../service/api';

const { Title, Text } = Typography;

// Helper para cores e iniciais de bancos comuns
const BANK_THEMES = {
    'ITAU': { color: '#ec6610', name: 'Itaú' },
    'NUBANK': { color: '#820ad1', name: 'Nubank' },
    'BRADESCO': { color: '#cc092f', name: 'Bradesco' },
    'SANTANDER': { color: '#ec0000', name: 'Santander' },
    'BANCO DO BRASIL': { color: '#fcf800', name: 'BB', textColor: '#0038a8' },
    'CAIXA': { color: '#006699', name: 'Caixa' },
    'INTER': { color: '#ff7a00', name: 'Inter' },
    'BTG': { color: '#001529', name: 'BTG' },
};

export default function OpenFinanceModal({ open, onClose }) {
    const [loading, setLoading] = useState(false);
    const [connections, setConnections] = useState([]);

    useEffect(() => {
        if (open) {
            fetchConnections();
        }
    }, [open]);

    const fetchConnections = async () => {
        setLoading(true);
        try {
            const res = await Method_POST_CONTEXTO("/cliente/conta-bancaria/listar");
            const allAccounts = Array.isArray(res) ? res : [];

            // Filtramos apenas as que possuem vínculo Pluggy (Open Finance)
            // Conforme estrutura da API, verificamos pluggyAccountId e pluggyConnectionId
            const openFinanceAccounts = allAccounts.filter(acc =>
                acc.pluggyAccountId ||
                acc.pluggyConnectionId ||
                acc.origem === 'OPEN_FINANCE'
            );

            setConnections(openFinanceAccounts);
        } catch (error) {
            console.error("Erro ao carregar conexões Open Finance:", error);
            message.error("Não foi possível carregar as conexões do Open Finance.");
        } finally {
            setLoading(false);
        }
    };

    const getBankTheme = (bankName = '') => {
        const upperName = bankName.toUpperCase();
        for (const [key, theme] of Object.entries(BANK_THEMES)) {
            if (upperName.includes(key)) return theme;
        }
        return { color: '#64748b', name: bankName.substring(0, 2) };
    };

    const handleConnectNew = () => {
        const companyStr = sessionStorage.getItem("COMPANY");

        let companyId = 1012;

        if (companyStr) {
            try {
                const company = JSON.parse(companyStr);
                if (company?.id) {
                    companyId = company.id;
                }
            } catch (e) { }
        }
        onClose();
        window.open(`/front/#Openfinance/${companyId}`, "_blank");
    };

    const syncAccount = async (id) => {
        message.loading({ content: 'Sincronizando dados...', key: 'sync' });
        try {
            // Endpoint fictício ou real de sincronização manual
            await Method_POST_CONTEXTO("/openfinance/sincronizar", { id });
            message.success({ content: 'Dados atualizados com sucesso!', key: 'sync' });
            fetchConnections();
        } catch (err) {
            message.error({ content: 'Falha na sincronização.', key: 'sync' });
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { type: "spring", stiffness: 300, damping: 25 }
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={580}
            className="premium-modal-v3 open-finance-modal"
            bodyStyle={{ padding: '0' }}
            style={{ borderRadius: '32px', overflow: 'hidden' }}
            closeIcon={<div className="modal-close-btn"><X size={18} /></div>}
        >
            <div className="open-finance-content" style={{ padding: '32px', background: '#fff' }}>
                {/* Header Section */}
                <header className="open-finance-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 40, gap: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                        <div className="header-icon-container" style={{
                            width: 56,
                            height: 56,
                            borderRadius: '18px',
                            backgroundColor: '#f5f3ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#7c3aed',
                            boxShadow: '0 10px 15px -3px rgba(124, 58, 237, 0.15)',
                            position: 'relative',
                            overflow: 'hidden',
                            flexShrink: 0
                        }}>
                            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 100%)' }} />
                            <Landmark size={28} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <Title level={3} className="header-title" style={{ margin: 0, fontWeight: 850, color: '#0f172a', letterSpacing: '-0.02em', fontSize: '24px' }}>
                                Contas Conectadas
                            </Title>
                            <Text className="header-subtitle" style={{ fontSize: '14px', fontWeight: 600, color: '#64748b' }}>
                                Tecnologia Open Finance
                            </Text>
                        </div>
                    </div>

                    <motion.div whileHover={{ scale: 1.1, rotate: 90 }} whileTap={{ scale: 0.9 }}>
                        <Button
                            type="primary"
                            shape="circle"
                            icon={<Plus size={26} />}
                            onClick={handleConnectNew}
                            className="add-connection-btn"
                            style={{
                                width: 48,
                                height: 48,
                                background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 12px 20px -5px rgba(124, 58, 237, 0.4)',
                                flexShrink: 0
                            }}
                        />
                    </motion.div>
                </header>

                {/* Connections List with Stagger Effect */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: '260px' }}
                >
                    {loading ? (
                        [1, 2, 3].map(i => <Skeleton.Button key={i} active block style={{ height: 88, borderRadius: 20, marginBottom: 14 }} />)
                    ) : connections.length > 0 ? (
                        connections.map((acc) => {
                            const theme = getBankTheme(acc.banco?.nome || acc.nome);
                            const bankCode = acc.banco?.codigoFEBRABAN;

                            return (
                                <motion.div
                                    key={acc.id}
                                    variants={itemVariants}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '20px',
                                        background: '#fff',
                                        borderRadius: '20px',
                                        border: '1px solid #f1f5f9',
                                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                    className="bank-connection-card"
                                >
                                    {/* Bank Logo Area */}
                                    <div className="bank-logo-container" style={{
                                        width: 52,
                                        height: 52,
                                        borderRadius: '14px',
                                        background: bankCode ? '#fff' : theme.color,
                                        marginRight: 20,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: theme.textColor || '#fff',
                                        fontSize: '13px',
                                        fontWeight: 900,
                                        boxShadow: bankCode ? '0 4px 10px rgba(0,0,0,0.05)' : `0 8px 16px -4px ${theme.color}44`,
                                        flexShrink: 0,
                                        border: bankCode ? '1px solid #f1f5f9' : 'none',
                                        padding: bankCode ? '8px' : '0'
                                    }}>
                                        {bankCode ? (
                                            <BankIcon bankId={bankCode} size={36} />
                                        ) : (
                                            theme.name.substring(0, 3).toUpperCase()
                                        )}
                                    </div>

                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <Text strong className="bank-name" style={{ fontSize: '15px', color: '#1e293b', display: 'block', marginBottom: 2 }}>
                                            {acc.banco?.nome || acc.nome}
                                        </Text>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Tag className="account-tag" style={{
                                                margin: 0,
                                                borderRadius: '6px',
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                background: '#f1f5f9',
                                                border: 'none',
                                                color: '#64748b'
                                            }}>
                                                {acc.conta ? `CC ${acc.conta}` : 'SYNCHRONIZED'}
                                            </Tag>
                                            <Badge status="success" />
                                        </div>
                                    </div>

                                    <div className="balance-container" style={{ textAlign: 'right', flexShrink: 0 }}>
                                        <div className="balance-value" style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
                                            {acc.saldo ? `R$ ${acc.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'R$ 0,00'}
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })
                    ) : (
                        <Empty description="Inicie uma conexão para ver seus dados aqui." />
                    )}
                </motion.div>

                {/* Footer Enhanced */}
                <footer className="open-finance-footer" style={{
                    marginTop: 40,
                    padding: '20px',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    border: '1px solid #e2e8f0'
                }}>
                    <div className="footer-status-icon" style={{
                        width: 32,
                        height: 32,
                        borderRadius: '10px',
                        background: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                        flexShrink: 0
                    }}>
                        <ShieldCheck size={18} style={{ color: '#10b981' }} />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <Text disabled className="footer-title" style={{ fontSize: '12px', fontWeight: 700, color: '#475569', display: 'block', lineHeight: 1 }}>
                            Segurança de Nível Bancário
                        </Text>
                        <Text disabled className="footer-subtitle" style={{ fontSize: '10px', fontWeight: 500, color: '#94a3b8' }}>
                            Criptografia AES-256 de ponta a ponta
                        </Text>
                    </div>
                </footer>
            </div>

            <style>{`
                .open-finance-modal .ant-modal-content {
                    border-radius: 32px !important;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.2) !important;
                }
                .bank-connection-card:hover {
                    transform: translateY(-4px) scale(1.01);
                    border-color: #7c3aed33 !important;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02) !important;
                }
                .bank-connection-card:hover .sync-btn {
                    color: #7c3aed !important;
                }
                .modal-close-btn {
                    background: #f1f5f9;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                    color: #64748b;
                }
                .modal-close-btn:hover {
                    background: #e2e8f0;
                    transform: rotate(90deg);
                    color: #0f172a;
                }
                .premium-modal-v3 .ant-modal-close {
                    top: 24px;
                    right: 24px;
                }

                @media (max-width: 576px) {
                    .open-finance-content {
                        padding: 24px 16px !important;
                    }
                    .open-finance-header {
                        margin-bottom: 24px !important;
                        gap: 12px !important;
                    }
                    .header-icon-container {
                        width: 44px !important;
                        height: 44px !important;
                        border-radius: 14px !important;
                    }
                    .header-icon-container svg {
                        width: 20px !important;
                        height: 20px !important;
                    }
                    .header-title {
                        font-size: 20px !important;
                        line-height: 1.2 !important;
                    }
                    .header-subtitle {
                        font-size: 11px !important;
                    }
                    .add-connection-btn {
                        width: 42px !important;
                        height: 42px !important;
                    }
                    .add-connection-btn svg {
                        width: 22px !important;
                        height: 22px !important;
                    }
                    .bank-connection-card {
                        padding: 16px 12px !important;
                        gap: 12px !important;
                        border-radius: 16px !important;
                    }
                    .bank-logo-container {
                        width: 44px !important;
                        height: 44px !important;
                        margin-right: 0 !important;
                        border-radius: 12px !important;
                    }
                    .bank-logo-container svg {
                        width: 28px !important;
                        height: 28px !important;
                    }
                    .bank-name {
                        font-size: 14px !important;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        max-width: 140px;
                    }
                    .account-tag {
                        font-size: 10px !important;
                        padding: 0 6px !important;
                    }
                    .balance-value {
                        font-size: 15px !important;
                    }
                    .open-finance-footer {
                        flex-direction: row;
                        text-align: left;
                        gap: 12px !important;
                        padding: 16px !important;
                        margin-top: 24px !important;
                    }
                    .footer-status-icon {
                        width: 28px !important;
                        height: 28px !important;
                    }
                    .footer-title {
                        font-size: 11px !important;
                        text-align: left;
                    }
                    .footer-subtitle {
                        font-size: 10px !important;
                        text-align: left;
                    }
                    .premium-modal-v3 .ant-modal-close {
                        top: 16px !important;
                        right: 16px !important;
                    }
                }
            `}</style>
        </Modal>
    );
}
